import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import toast from "react-hot-toast";
import { useGameStore } from "../store/gameStore";
import fireMonster from "../images/monsters/fire-monster.png";
import wolfMonster from "../images/monsters/wolf-monster.png";
import gasMonster from "../images/monsters/gas-monster.png";
import slipperyMonster from "../images/monsters/slippery-monster.png";
import electricMonster from "../images/monsters/electric-monster.png";
import stairMonster from "../images/monsters/stair-monster.png";
import elbeMonster from "../images/monsters/elbe-monster.png";
import pointedMonster from "../images/monsters/pointed-monster.png";

const Quiz = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [monsterType, setMonsterType] = useState(null);
  const [isAlreadyCompleted, setIsAlreadyCompleted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30);
  const [modalTimeLeft, setModalTimeLeft] = useState(10);
  const [showFailModal, setShowFailModal] = useState(false);
  const [isTimerActive, setIsTimerActive] = useState(false);

  const timerRef = useRef(null);
  const { answeredQuestions, completeQuiz, userName } = useGameStore();

  // 몬스터별 퀴즈 문제 데이터
  const monsterQuizData = useMemo(
    () => ({
      fire: {
        name: "화재 몬스터",
        image: fireMonster,
        questions: [
          {
            question: "소화기의 가장 중요한 역할은 무엇일까요?",
            options: [
              "불길을 꺼서 피해를 막는다",
              "교실을 깨끗하게 한다",
              "친구를 놀라게 한다",
              "문을 잠근다",
            ],
            correctAnswer: 0,
            explanation: "소화기는 불길을 꺼서 피해를 막는 것이 가장 중요해요",
          },
          {
            question:
              "야외에서 불을 발견했을 때 하면 안 되는 행동은 무엇일까요?",
            options: [
              "어른에게 알린다",
              "불 근처에 가까이 가서 사진을 찍는다",
              "안전한 곳으로 대피한다",
              "119에 신고한다",
            ],
            correctAnswer: 1,
            explanation:
              "야외에서 불을 발견했을 때는 어른에게 알리고 안전한 곳으로 대피해야 해요",
          },
        ],
      },
      wolf: {
        name: "추행 몬스터",
        image: wolfMonster,
        questions: [
          {
            question: "모르는 사람이 몸을 만지려고 하면 어떻게 해야 할까요?",
            options: [
              "따라간다",
              "그냥 가만히 있는다",
              "큰 소리로 싫다고 말한다",
              "장난이라고 생각한다",
            ],
            correctAnswer: 2,
            explanation:
              "모르는 사람이 몸을 만지려고 하면 강하게 거절하고 소리쳐야 해요",
          },
          {
            question:
              "인터넷이나 휴대폰에서 부적절한 사진을 보내달라는 요구를 받았을 때 올바른 대처는?",
            options: [
              "그냥 무시하고 저장한다",
              "재미있게 따라준다",
              "친구에게만 보여준다",
              "거절하고 어른에게 알린다",
            ],
            correctAnswer: 3,
            explanation:
              "누구나 부적절한 사진을 보내달라거나 요청하면 이를 거절해야 해요",
          },
        ],
      },
      gas: {
        name: "가스 몬스터",
        image: gasMonster,
        questions: [
          {
            question: "출입금지 표지판을 보았을 때 올바른 행동은 무엇일까요?",
            options: [
              "들어가지 않고 선생님이나 어른에게 알린다",
              "몰래 들어가서 탐험한다",
              "친구와 숨바꼭질한다",
              "장난삼아 문을 열어본다",
            ],
            correctAnswer: 0,
            explanation:
              "출입금지 표지판을 보았을 때는 들어가지 않고 선생님이나 어른에게 알려야 해요",
          },
          {
            question:
              "집에서 가스 냄새가 나면 가장 먼저 해야 할 일은 무엇일까요?",
            options: [
              "화장실로 대피한다",
              "선풍기를 켠다",
              "창문을 열어 환기한다",
              "아무 일 없는 척한다",
            ],
            correctAnswer: 2,
            explanation: "집에서 가스 냄새가 나면 창문을 열어 환기해야 해요",
          },
        ],
      },
      slippery: {
        name: "미끌 몬스터",
        image: slipperyMonster,
        questions: [
          {
            question: "실내에서 뛰면 가장 큰 위험은 무엇일까요?",
            options: [
              "주변 물건에 부딪혀 다칠 수 있다",
              "더 빨리 이동할 수 있다",
              "체력이 좋아진다",
              "친구들이 즐거워한다",
            ],
            correctAnswer: 0,
            explanation: "실내에서 뛰면 주변 물건에 부딪혀 크게 다칠 수 있어요",
          },
          {
            question: "잔디밭에서 안전하게 놀기 위한 가장 중요한 습관은?",
            options: [
              "물을 뿌려 더 미끄럽게 한다",
              "조심스럽게 움직이고 장난치지 않는다",
              "일부러 미끄러져 본다",
              "난간이나 언덕에서 뛰어내린다",
            ],
            correctAnswer: 1,
            explanation:
              "잔디밭에서 안전하게 놀기 위해서는 조심스럽게 움직이고 장난치지 않아야 해요",
          },
        ],
      },
      electric: {
        name: "전기 몬스터",
        image: electricMonster,
        questions: [
          {
            question: "콘센트를 사용할 때 올바른 방법은 무엇일까요?",
            options: [
              "여러 개의 코드를 꽂는다",
              "젖은 손으로 잡는다",
              "필요한 기기만 꽂는다",
              "발로 차 본다",
            ],
            correctAnswer: 2,
            explanation: "콘센트를 사용할 때는 필요한 기기만 꽂아야 해요",
          },
          {
            question:
              "야외에서 떨어진 전선을 보았을 때 올바른 행동은 무엇일까요?",
            options: [
              "가까이 가서 만져본다",
              "친구와 장난으로 건드린다",
              "멀리 떨어져 피해간다",
              "발로 차 본다",
            ],
            correctAnswer: 2,
            explanation:
              "야외에서 떨어진 전선을 보았을 때는 멀리 떨어져 피해가야 해요",
          },
        ],
      },
      stair: {
        name: "계단 몬스터",
        image: stairMonster,
        questions: [
          {
            question: "계단을 내려갈 때 가장 안전한 방법은 무엇일까요?",
            options: [
              "눈을 감고 내려간다",
              "친구를 밀며 내려간다",
              "손잡이를 잡고 천천히 내려간다",
              "뛰어서 빨리 내려간다",
            ],
            correctAnswer: 2,
            explanation:
              "계단을 내려갈 때는 손잡이를 잡고 천천히 내려가야 해요",
          },
          {
            question: "난간에 서서 밖을 내려다보면 위험한 이유는 무엇일까요?",
            options: [
              "바람에 밀려 떨어질 수 있다",
              "경치를 잘 볼 수 있다",
              "재미있다",
              "친구를 부를 수 있다",
            ],
            correctAnswer: 0,
            explanation:
              "난간에 서서 밖을 내려다보면 바람에 밀려 떨어질 수 있어요",
          },
        ],
      },
      elbe: {
        name: "엘베 몬스터",
        image: elbeMonster,
        questions: [
          {
            question: "엘리베이터가 갑자기 멈췄을 때 올바른 행동은 무엇일까요",
            options: [
              "큰 소리로 장난을 친다",
              "문을 억지로 열려고 한다",
              "뛰어내리려고 한다",
              "비상 버튼을 누르고 구조를 기다린다",
            ],
            correctAnswer: 3,
            explanation:
              "엘리베이터가 갑자기 멈췄을 때는 비상 버튼을 누르고 구조를 기다려야 해요",
          },
          {
            question:
              "엘리베이터에서 가장 올바른 행동은 무엇일까요?",
            options: [
              "안전을 위해 가급적 혼자 엘리베이터를 탄다",
              "엘리베이터 손잡이를 잡고있는다",
              "문을 더 빨리 닫기위해 힘으로 닫는다",
              "비상 버튼이 잘 작동하는지 눌러서 테스트해본다",
            ],
            correctAnswer: 1,
            explanation:
              "엘리베이터에서 가장 올바른 행동은 엘리베이터 손잡이를 잡고있어야 해요",
          },
        ],
      },
      pointed: {
        name: "뾰족 몬스터",
        image: pointedMonster,
        questions: [
          {
            question: "구조물이 많은 실내에서 뛰면 왜 조심해야 할까요?",
            options: [
              "뾰족한 모서리에 부딪힐 수 있다",
              "숙제를 빨리 끝낼 수 있다",
              "선생님이 상을 준다",
              "집에 빨리 갈 수 있다",
            ],
            correctAnswer: 0,
            explanation:
              "구조물이 많은 실내에서 뛰면 뾰족한 모서리에 부딪힐 수 있어요",
          },
          {
            question:
              "손에 나무 가시가 박히면 가장 먼저 해야 할 일은 무엇일까요?",
            options: [
              "계속 만지며 장난친다",
              "뛰어다니며 논다",
              "입으로 빼려고 한다",
              "그대로 두고 어른에게 알린다",
            ],
            correctAnswer: 3,
            explanation:
              "손에 나무 가시가 박히면 그대로 두고 어른에게 알려야 해요",
          },
        ],
      },
    }),
    []
  );

  // 공간별 고정 문제 선택
  const selectFixedQuestion = useCallback(
    (monster, space) => {
      const allQuestions = monsterQuizData[monster].questions;

      let questionIndex;
      if (space === "indoor") {
        questionIndex = 0;
      } else if (space === "outdoor") {
        questionIndex = 1;
      } else {
        navigate("/");
        return;
      }

      const selectedQuestion = allQuestions[questionIndex];

      setCurrentQuestion({
        ...selectedQuestion,
        originalIndex: questionIndex,
      });

      setIsTimerActive(true);
    },
    [monsterQuizData, navigate]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const monster = params.get("monster");
    const space = params.get("space"); // 'indoor' 또는 'outdoor'

    if (monster && monsterQuizData[monster] && space) {
      setMonsterType(monster);

      // 공간별 고정 문제 출제
      selectFixedQuestion(monster, space);
    } else {
      navigate("/");
    }
  }, [location, monsterQuizData, navigate, selectFixedQuestion]);

  useEffect(() => {
    if (currentQuestion && answeredQuestions[monsterType]) {
      const isCompleted = answeredQuestions[monsterType].includes(currentQuestion.originalIndex);
      setIsAlreadyCompleted(isCompleted);
    }
  }, [currentQuestion, answeredQuestions, monsterType]);

  // 30초 타이머
  useEffect(() => {
    if (currentQuestion && isTimerActive) {
      setTimeLeft(30);

      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setIsTimerActive(false);
            setShowFailModal(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      timerRef.current = timer;

      return () => {
        clearInterval(timer);
        timerRef.current = null;
      };
    }
  }, [currentQuestion, isTimerActive]);

  useEffect(() => {
    if (showFailModal) {
      setModalTimeLeft(10);
      const modalTimer = setInterval(() => {
        setModalTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(modalTimer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(modalTimer);
    }
  }, [showFailModal]);

  const handleAnswerSelect = (answerIndex) => {
    if (isAnswered || !isTimerActive) return;

    setSelectedAnswer(answerIndex);
    setIsAnswered(true);

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsTimerActive(false);

    const isCorrect = answerIndex === currentQuestion.correctAnswer;

    if (isCorrect) {
      // store에 퀴즈 완료 상태 저장
      completeQuiz(monsterType, currentQuestion.originalIndex);

      // 정답 토스트 메시지
      if (isAlreadyCompleted) {
        toast.success(`🎉 정답입니다! 이미 푼 문제였지만 잘 풀었어요!`);
      } else {
        toast.success(
          `🎉 정답입니다! ${monsterQuizData[monsterType]?.name}를 물리쳤습니다!`
        );
      }
      setTimeout(() => {
        navigate("/monster-encyclopedia");
      }, 4000);
    } else {
      // 오답 시 실패 모달 표시
      setShowFailModal(true);
      setIsTimerActive(false);
    }
  };

  const handleCloseFailModal = () => {
    setShowFailModal(false);
    setSelectedAnswer(null);
    setIsAnswered(false);
    setTimeLeft(30);
    setIsTimerActive(true);
  };

  // 결제 안내 화면 - 시작 페이지를 거치지 않고 직접 접속한 경우
  if (!userName) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full text-center">
          <div className="mb-6">
            <div className="text-6xl mb-4">🔒</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              안전히어로 탐험대는 <br />결제 후 이용 가능합니다.
            </h2>
            <p className="text-gray-600 mb-6">
              프로그램을 이용하시려면 결제가 필요합니다.
            </p>
          </div>
          <button
            onClick={() => {
              window.open(
                "https://smartstore.naver.com/cremond/products/12521442463",
                "_blank"
              );
            }}
            className="w-full px-8 py-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-lg font-bold rounded-xl shadow-lg transform transition-all duration-300 hover:scale-105 active:scale-95"
          >
            프로그램 상세보기
          </button>
        </div>
      </div>
    );
  }

  if (!monsterType || !currentQuestion) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 relative">
      <header className="text-center pb-6">
        <div className="mb-4">
          <h1 className={`text-[34px] leading-10 font-bold mt-2 mb-6 drop-shadow-lg`}>
            {monsterQuizData[monsterType]?.name}가 나타났다!!
          </h1>
        </div>
      </header>

      <div className="relative max-w-2xl mx-auto">
        {/* 몬스터 이미지 */}
        <div className="flex justify-center">
          <div className="w-[390px] h-[400px] relative -top-14">
            <img
              src={monsterQuizData[monsterType]?.image}
              alt={monsterQuizData[monsterType]?.name}
              className="w-full h-full object-contain drop-shadow-2xl"
            />
          </div>
        </div>

        {/* 문제 카드 */}
        <div className="relative -mt-52 z-10 bg-white backdrop-blur-sm rounded-2xl shadow-2xl px-6 py-4 mb-6 border border-white/20">
          <div>
            <h2 className="text-xl font-bold text-gray-800 mb-3 text-left leading-relaxed">
              Q. {currentQuestion.question}
            </h2>

            {/* 답안 옵션들 */}
            <div className="space-y-2">
              {currentQuestion.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(index)}
                  disabled={isAnswered}
                  className={`w-full px-5 py-3 text-left rounded-xl border-2 transition-all duration-300 shadow-lg hover:shadow-xl ${
                    isAnswered
                      ? selectedAnswer === currentQuestion.correctAnswer && index === selectedAnswer
                        ? "border-green-500 bg-green-50 text-green-800 shadow-green-200"
                        : index === selectedAnswer && index !== currentQuestion.correctAnswer
                        ? "border-red-500 bg-red-50 text-red-800 shadow-red-200"
                        : "border-gray-200 bg-gray-50 text-gray-600"
                      : selectedAnswer === index
                      ? "border-blue-500 bg-blue-50 text-blue-800 shadow-blue-200"
                      : "border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50 hover:scale-[1.02]"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">
                      {index + 1}. {option}
                    </span>
                    {isAnswered && (
                      <>
                        {/* 정답일 때만 정답 아이콘 표시 */}
                        {selectedAnswer === currentQuestion.correctAnswer && (
                          <CheckCircle className="w-6 h-6 text-green-500" />
                        )}
                        {/* 오답일 때만 오답 아이콘 표시 */}
                        {selectedAnswer !== currentQuestion.correctAnswer && (
                          <XCircle className="w-6 h-6 text-red-500" />
                        )}
                      </>
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* 설명 */}
            {isAnswered && selectedAnswer === currentQuestion.correctAnswer && (
              <div className="mt-2 px-4 py-2 bg-blue-50 rounded-lg border border-blue-200 text-left">
                <p className="text-blue-800 font-medium">
                  💡설명: {currentQuestion.explanation}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 시간 카운팅 - 답변하지 않았을 때만 표시 */}
      {!isAnswered && (
        <div className="text-center mt-8">
          <div className="mb-4">
            <span
              className={`text-2xl font-bold px-6 py-3 rounded-xl ${
                timeLeft > 5
                  ? "bg-green-100 text-green-800"
                  : timeLeft > 2
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              ⏰ {Math.floor(timeLeft / 60)}:
              {(timeLeft % 60).toString().padStart(2, "0")}
            </span>
          </div>
        </div>
      )}

      {/* 실패 모달 */}
      {showFailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center">
            <div className="text-6xl mb-4">😵</div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              아직은 초보 히어로군!
            </h3>
            <p className="text-gray-600 mb-2">이 정도 안전 상식으로는</p>
            <p className="text-gray-600 mb-4">안전히어로가 될 수 없어!</p>
            <div className="text-3xl font-bold text-red-500 mb-4">
              ⏰ {Math.floor(modalTimeLeft / 60)}:
              {(modalTimeLeft % 60).toString().padStart(2, "0")}
            </div>
            <p className="text-sm text-gray-500 mb-6">
              10초 후 다시 정답을 선택하실 수 있습니다.
            </p>
            <button
              onClick={handleCloseFailModal}
              disabled={modalTimeLeft > 0}
              className={`px-6 py-3 font-bold rounded-lg transition-colors ${
                modalTimeLeft > 0
                  ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                  : "bg-blue-500 hover:bg-blue-600 text-white"
              }`}
            >
              {modalTimeLeft > 0
                ? `${modalTimeLeft}초 대기 중...`
                : "다시 도전하기"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Quiz;